import type { CSSProperties, FC } from 'react';
import { Col, Row } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { AvatarSkeleton, ButtonSkeleton } from '@/shared/components/skeletons';

import { ACTIVITY_TIMELINE_MARKER_COLUMN_WIDTH, ACTIVITY_TIMELINE_MARKER_SIZE } from '../ActivityTimelineItem';

interface ActivityTimelineSkeletonProps {
    count?: number;
    density?: 'compact' | 'regular';
    showAction?: boolean;
    showMetadataAction?: boolean;
    style?: CSSProperties;
}

/**
 * Loading geometry for timeline-based activity surfaces. It intentionally
 * mirrors ActivityTimelineItem so initial and paginated loading do not shift
 * markers, enriched identities, metadata, or trailing actions.
 */
const ActivityTimelineSkeleton: FC<ActivityTimelineSkeletonProps> = ({
    count = 5,
    density = 'regular',
    showAction = true,
    showMetadataAction = false,
    style,
}) => {
    const { token } = useAntDesignTheme();
    const spacing = density === 'compact' ? '0.375rem' : '0.875rem';

    return (
        <div aria-label='Loading activity' data-activity-timeline-skeleton='true' role='status' style={style}>
            {Array.from({ length: count }, (_, index) => {
                const isLast = index === count - 1;

                return (
                    <Row
                        align='top'
                        data-activity-timeline-skeleton-item='true'
                        key={index}
                        wrap={false}
                        style={{
                            minWidth: 0,
                            paddingBottom: isLast ? 0 : spacing,
                            position: 'relative',
                            width: '100%',
                        }}
                    >
                        {!isLast && (
                            <div
                                aria-hidden='true'
                                data-activity-timeline-skeleton-connector='true'
                                style={{
                                    background: token.colorBorder,
                                    bottom: -(ACTIVITY_TIMELINE_MARKER_SIZE / 2),
                                    left: ACTIVITY_TIMELINE_MARKER_COLUMN_WIDTH / 2,
                                    position: 'absolute',
                                    top: ACTIVITY_TIMELINE_MARKER_SIZE / 2,
                                    transform: 'translateX(-0.5px)',
                                    width: 1,
                                }}
                            />
                        )}
                        <Col
                            flex={`${ACTIVITY_TIMELINE_MARKER_COLUMN_WIDTH}px`}
                            style={{ display: 'flex', justifyContent: 'center', position: 'relative', zIndex: 1 }}
                        >
                            <AvatarSkeleton size={ACTIVITY_TIMELINE_MARKER_SIZE} />
                        </Col>
                        <Col flex='auto' style={{ minWidth: 0, paddingLeft: '0.75rem' }}>
                            <Row align='middle' gutter={8} wrap={false}>
                                <Col flex='auto' style={{ minWidth: 0 }}>
                                    <div style={{ alignItems: 'center', display: 'flex', gap: '0.25rem', minWidth: 0 }}>
                                        <ButtonSkeleton
                                            size='small'
                                            style={{ height: '1.5rem', minWidth: '7.5rem', width: '24%' }}
                                        />
                                        <ButtonSkeleton size='small' style={{ height: '0.75rem', width: '4.5rem' }} />
                                        <ButtonSkeleton
                                            size='small'
                                            style={{ height: '1.5rem', minWidth: '5.5rem', width: '20%' }}
                                        />
                                    </div>
                                </Col>
                                {showAction && (
                                    <Col flex='none'>
                                        <ButtonSkeleton shape='square' size='small' style={{ height: 24, width: 24 }} />
                                    </Col>
                                )}
                            </Row>
                            <Row align='middle' style={{ marginTop: '0.125rem', minWidth: 0 }} wrap={false}>
                                {showMetadataAction && (
                                    <Col flex='none'>
                                        <ButtonSkeleton size='small' style={{ height: 24, width: '7.75rem' }} />
                                    </Col>
                                )}
                                <Col flex='auto' style={{ minWidth: 0 }}>
                                    <ButtonSkeleton
                                        block
                                        size='small'
                                        style={{
                                            display: 'block',
                                            height: '0.625rem',
                                            maxWidth: '16rem',
                                            width: '42%',
                                        }}
                                    />
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                );
            })}
        </div>
    );
};

export default ActivityTimelineSkeleton;
